import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from "next";
import Link from "next/link";
import dynamic from "next/dynamic";
import { VFC } from "react";

import { NextSeo } from "next-seo";
import Layout from "components/layout";
import UserData from "components/users/userData";
import MyPageHeader from "components/users/myPageHeader";
import { ArticleCard } from "components/utils";

import { admin_db } from "lib/firebase-admin";
import { signin } from "lib/signin";
import { convertDate } from "lib/utils";
import { useUser } from "hooks/useUser";
import { useDraft } from "hooks/users/useDraft";
import { useFollow } from "hooks/articles/useFollow";
import { useClientFetchUser } from "hooks/users/useClientFetchUser";

import { CurrentUser, IndexArticle } from "lib/type";

import { FaEdit, FaRegEdit } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import { RiUserAddFill } from "react-icons/ri";

const DraftArticles = dynamic(
  () => import("components/articles/draftArticles")
);

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

let author: CurrentUser = {
    uid: "",
    name: "",
    avatar: "",
    color: "#ffffff",
    isSendNotif: false,
    email: "",
    bio: "",
    links: [""],
    totalLikeCount: 0,
    followerCount: 0,
  },
  articles: IndexArticle[] = [
    {
      articleID: "",
      title: "",
      thumb: "",
      color: "#ffffff",
      tags: [""],
      createdAt: "",
    },
  ];

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const uid = context.params?.uid;
  if (!uid || typeof uid !== "string") return { props: { author, articles } };
  const querySnapShot = await admin_db
    .collection("users")
    .where("uid", "==", uid)
    .get()
    .catch(() => null);
  if (!querySnapShot || querySnapShot.docs.length !== 1)
    return { props: { author, articles } };
  const users = querySnapShot.docs.map((doc) => doc.data());
  author = await JSON.parse(JSON.stringify(users[0]));
  if (!author) return { props: { author, articles } };
  const articleDocs = await admin_db
    .collection("users")
    .doc(author.uid)
    .collection("publicArticles")
    .orderBy("createdAt", "desc")
    .limit(10)
    .get()
    .catch((err: Error) => {
      console.log(err);
      return null;
    });
  if (!articleDocs) return { props: { author, articles } };
  articles = await Promise.all(
    articleDocs.docs.map(async (articleDoc) => {
      const article = articleDoc.data();
      const { title, thumb, color, createdAt, updateAt, tags, likeCount } =
        article;
      const crateDate = convertDate(createdAt);
      const updateDate = convertDate(updateAt);
      return {
        articleID: articleDoc.id,
        title: title,
        thumb: thumb,
        color: color,
        createdAt: crateDate,
        updateAt: updateDate,
        tags: tags,
        likeCount: likeCount,
      };
    })
  );
  if (articles.length < 1) return { props: { author, articles } };

  return { props: { author, articles }, revalidate: 3600 };
};

type UserPageProps = InferGetStaticPropsType<typeof getStaticProps>;

const UserPage: VFC<UserPageProps> = (props) => {
  const { user, isAuthChecking } = useUser();

  const { author, articles } = useClientFetchUser(props, user?.uid);

  const publicArticles = articles
    ?.map((article) => {
      if (!article.articleID) return;
      return <ArticleCard key={article.articleID} article={article} />;
    })
    .filter((article) => article);

  const { drafts } = useDraft(isAuthChecking, user, author);
  const draftArticles = drafts
    ?.map((article) => {
      if (!article) return;
      const { articleID, thumb, title, color, createdAt } = article;
      return (
        <li
          key={article.articleID}
          className="shadow-light-700 dark:shadow-dark-400 border-l-[0.5rem] border-solid shadow-lg"
          style={{ borderColor: color }}
        >
          <div className="overflow-hidden">
            <Link href={`/articles/${articleID}/edit`}>
              <a className="relative block p-4">
                <p className="absolute right-0 top-1/2 -translate-y-1/2 rotate-12 transform text-6xl opacity-20">
                  {thumb}
                </p>
                <h3 className="mb-2 text-xl">{title}</h3>
                <p className="flex items-center">
                  <FiClock className="mr-1" />
                  {createdAt}
                </p>
              </a>
            </Link>
          </div>
        </li>
      );
    })
    .filter((draft) => draft);

  const { isFollowChecking, isFollowing, changeFollow } = useFollow(
    author,
    user
  );

  const meta = {
    noindex: true,
    title: author?.name,
    description: author?.bio,
    canonical: `${process.env.NEXT_PUBLIC_WEB_URL}/users/${author?.uid}`,
  };

  return (
    <Layout
      lists={[
        {
          string: (author as any).name,
        },
      ]}
    >
      <NextSeo {...meta} />
      {author && (
        <>
          <UserData author={author} />
          {user?.uid === author.uid ? (
            <MyPageHeader user={user} />
          ) : (
            <div className={`container mb-6 py-4 lg:px-0`}>
              {isAuthChecking && <div className="h-8" />}
              {!isAuthChecking && !isFollowChecking && (
                <button
                  onClick={user ? changeFollow : signin}
                  className={`flex items-center rounded-md px-4 py-2 ${
                    isFollowing
                      ? "border-dark dark:border-light border border-solid"
                      : "bg-dark text-light dark:bg-light dark:text-dark"
                  }`}
                >
                  <RiUserAddFill className="mr-1 text-xl" />
                  {user
                    ? isFollowing
                      ? "フォロー中"
                      : "フォローする"
                    : "サインインしてフォローする"}
                </button>
              )}
            </div>
          )}
          <section className="mb-8 md:container lg:px-0">
            <h2 className="mx-4 mb-4 flex items-center text-xl md:mx-0">
              <FaEdit className="mr-1" />
              書いた記事
            </h2>
            {publicArticles.length > 0 && (
              <ul className="space-y-4 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                {publicArticles}
              </ul>
            )}
            {publicArticles.length < 1 && (
              <p className="px-4">まだ記事はありません。</p>
            )}
          </section>
          <DraftArticles {...{ isAuthChecking, user, author }} />
        </>
      )}
    </Layout>
  );
};

export default UserPage;
